import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  Copy,
  Check,
  ArrowRight,
  Sparkles,
  Video,
  Loader2,
  MapPin,
  LogIn,
} from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import type { PreviewPageData } from '../../types/listing-funnel';
import {
  PREVIEW_NOTE,
  SIGNUP_CTA,
  DOWNLOAD_CTA,
  DOWNLOAD_CTA_AUTH,
  FULL_VERSION_CTA,
  UPSELL_HEADLINE,
  UPSELL_TOOLS,
  FUNNEL_BASE_PATH,
} from './constants';
import {
  fetchListingPreviewBySlug,
  downloadPreviewVideo,
  logListingEvent,
} from '../../services/listing-funnel';

interface ListingPreviewPageProps {
  shareSlug: string;
  user: User | null;
  onSignUp: () => void;
  onLogin: () => void;
}

const ListingPreviewPage: React.FC<ListingPreviewPageProps> = ({
  shareSlug,
  user,
  onSignUp,
  onLogin,
}) => {
  const [preview, setPreview] = useState<PreviewPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await fetchListingPreviewBySlug(shareSlug);
      if (data) {
        setPreview(data);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [shareSlug]);

  const shareUrl = `${window.location.origin}${FUNNEL_BASE_PATH}/preview/${shareSlug}`;

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      logListingEvent('share_link_copied', { shareSlug });
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard API may fail in some contexts */ }
  }, [shareUrl, shareSlug]);

  const handleDownload = useCallback(async () => {
    if (!user) {
      logListingEvent('preview_download_attempted', { shareSlug });
      onSignUp();
      return;
    }

    if (!preview?.videoUrl) return;

    setDownloading(true);
    try {
      const filename = preview.listing.title
        ? `${preview.listing.title.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 50)}_preview.mp4`
        : 'imagemotion-preview.mp4';
      await downloadPreviewVideo(preview.videoUrl, filename);
      logListingEvent('preview_downloaded', { shareSlug });
    } catch {
      // Silently handle download failure
    } finally {
      setDownloading(false);
    }
  }, [user, preview, shareSlug, onSignUp]);

  const handleFullVersion = useCallback(() => {
    logListingEvent('full_version_clicked', { shareSlug });
    if (user) {
      window.location.href = '/';
    } else {
      onSignUp();
    }
  }, [user, shareSlug, onSignUp]);

  const handleSignupClick = useCallback(() => {
    logListingEvent('signup_clicked', { shareSlug });
    onSignUp();
  }, [shareSlug, onSignUp]);

  // --- Loading ---
  if (loading) {
    return (
      <main className="relative z-10">
        <div className="container mx-auto px-4 py-32 text-center">
          <Loader2 size={32} className="mx-auto text-slate-400 animate-spin mb-4" />
          <p className="text-slate-400">Loading preview...</p>
        </div>
      </main>
    );
  }

  // --- Not Found ---
  if (notFound || !preview) {
    return (
      <main className="relative z-10">
        <div className="container mx-auto px-4 py-32 text-center">
          <Video size={48} className="mx-auto text-slate-600 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Preview not found</h1>
          <p className="text-slate-400 mb-8">This preview may have expired or the link is invalid.</p>
          <a
            href={FUNNEL_BASE_PATH}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-medium transition-all"
          >
            Create a new preview
            <ArrowRight size={16} />
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">

          {/* Listing Title / Address */}
          {(preview.listing.title || preview.listing.address) && (
            <div className="mb-6 text-center">
              {preview.listing.title && (
                <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
                  {preview.listing.title}
                </h1>
              )}
              {preview.listing.address && (
                <p className="inline-flex items-center gap-1.5 text-sm text-slate-400">
                  <MapPin size={14} />
                  {preview.listing.address}
                </p>
              )}
            </div>
          )}

          {/* Video Player */}
          <div className="relative rounded-2xl overflow-hidden bg-black border border-slate-700/60 mb-6">
            <video
              src={preview.videoUrl}
              controls
              controlsList="nodownload"
              playsInline
              className="w-full max-h-[70vh]"
              style={{ objectFit: 'contain' }}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Preview Note */}
          <p className="text-center text-sm text-slate-400 mb-6">
            {PREVIEW_NOTE}
          </p>

          {/* Source Images Strip */}
          {preview.images.length > 0 && (
            <div className="flex items-center justify-center gap-3 mb-8">
              {preview.images.map((img, i) => (
                <div
                  key={i}
                  className="w-28 h-20 md:w-36 md:h-24 rounded-lg overflow-hidden border border-slate-700/60 bg-slate-800"
                >
                  <img
                    src={img}
                    alt={`Source image ${i + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            {/* Download / Signup CTA */}
            {user ? (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 text-white rounded-xl font-semibold text-base transition-all shadow-xl shadow-blue-900/40"
              >
                {downloading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Download size={18} />
                )}
                {DOWNLOAD_CTA_AUTH}
              </button>
            ) : (
              <button
                onClick={handleSignupClick}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold text-base transition-all shadow-xl shadow-blue-900/40"
              >
                <Sparkles size={18} />
                {DOWNLOAD_CTA}
              </button>
            )}

            {/* Full version CTA */}
            <button
              onClick={handleFullVersion}
              className="flex items-center gap-2 px-6 py-4 bg-slate-900/80 border-2 border-slate-600 hover:border-slate-500 text-white rounded-xl font-medium text-base transition-colors"
            >
              {FULL_VERSION_CTA}
              <ArrowRight size={16} />
            </button>

            {/* Share link */}
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-5 py-4 bg-slate-900/60 border border-slate-700 hover:border-slate-600 text-slate-300 rounded-xl text-sm font-medium transition-colors"
            >
              {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>

          {/* Signup prompt for anonymous users */}
          {!user && (
            <div className="max-w-lg mx-auto text-center mb-12 p-5 bg-slate-900/60 border border-slate-700/60 rounded-xl">
              <p className="text-slate-300 text-sm mb-3">{SIGNUP_CTA}</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={handleSignupClick}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Create free account
                </button>
                <button
                  onClick={onLogin}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <LogIn size={14} />
                  Sign in
                </button>
              </div>
            </div>
          )}

          {/* ReHub Upsell */}
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/40 border border-slate-700/60 rounded-2xl p-8 md:p-10">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2 text-center">
              {UPSELL_HEADLINE}
            </h2>
            <p className="text-slate-400 text-center mb-8 text-sm">
              Unlock full videos from all listing photos, branding options, and more tools inside ReHub.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {UPSELL_TOOLS.map((tool) => (
                <a
                  key={tool.name}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-800/30 hover:bg-slate-800/50 border border-transparent hover:border-blue-500/20 rounded-lg transition-all group"
                >
                  <div className="w-14 min-w-[3.5rem] h-9 rounded-md bg-slate-800/60 flex items-center justify-center shrink-0 p-1">
                    <img src={tool.logo} alt="" className="max-h-full max-w-full w-auto object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-200 group-hover:text-blue-300 transition-colors">{tool.name}</p>
                    <p className="text-xs text-slate-400 line-clamp-2">{tool.description}</p>
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center">
              <a
                href="https://hub.rehub.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 rounded-xl text-sm font-medium text-white transition-colors"
              >
                Explore ReHub
                <ArrowRight size={14} />
              </a>
            </div>
          </div>

          {/* Create another */}
          <div className="text-center mt-10">
            <a
              href={FUNNEL_BASE_PATH}
              className="text-sm text-slate-400 hover:text-slate-300 underline underline-offset-4 transition-colors"
            >
              Create another preview from a different listing
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ListingPreviewPage;
